import { Calculator, DollarSign, Percent } from "lucide-react";

interface TaxLoaderInlineProps {
  message?: string;
}

export function TaxLoader({ message = "Generating tax computation..." }: TaxLoaderInlineProps) {
  return (
    <div style={{
      background: "var(--bg-color)",
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "var(--color)"
    }}>
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "2rem"
      }}>
        {/* Animated Rings with Icons */}
        <div style={{
          position: "relative",
          width: "200px",
          height: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          {/* Outer Ring */}
          <div className="spinner-ring-outer" style={{
            position: "absolute",
            width: "160px",
            height: "160px",
            border: "3px solid transparent",
            borderTopColor: "#3b82f6",
            borderRadius: "50%",
            animation: "spin 3s linear infinite"
          }} />

          {/* Middle Ring */}
          <div className="spinner-ring-middle" style={{
            position: "absolute",
            width: "120px",
            height: "120px",
            border: "3px solid transparent",
            borderTopColor: "#10b981",
            borderRadius: "50%",
            animation: "spin-reverse 2s linear infinite"
          }} />

          {/* Inner Ring */}
          <div className="spinner-ring-inner" style={{
            position: "absolute",
            width: "80px",
            height: "80px",
            border: "3px solid transparent",
            borderTopColor: "#8b5cf6",
            borderRadius: "50%",
            animation: "spin 1.5s linear infinite"
          }} />

          {/* Orbiting Icons */}
          <div style={{
            position: "absolute",
            width: "160px",
            height: "160px",
            animation: "spin 3s linear infinite"
          }}>
            <Calculator 
              style={{
                position: "absolute",
                top: "-12px",
                left: "50%",
                transform: "translateX(-50%)",
                color: "#3b82f6",
                width: "24px",
                height: "24px"
              }}
            />
          </div>

          <div style={{
            position: "absolute",
            width: "160px",
            height: "160px",
            animation: "spin-reverse 2.5s linear infinite"
          }}>
            <DollarSign 
              style={{
                position: "absolute",
                bottom: "-12px",
                left: "50%",
                transform: "translateX(-50%)",
                color: "#10b981",
                width: "24px",
                height: "24px"
              }}
            />
          </div>

          <div style={{
            position: "absolute",
            width: "160px",
            height: "160px",
            animation: "spin 2s linear infinite"
          }}>
            <Percent 
              style={{
                position: "absolute",
                top: "50%",
                right: "-12px",
                transform: "translateY(-50%)",
                color: "#8b5cf6",
                width: "24px",
                height: "24px"
              }}
            />
          </div>

          {/* Center Calculator Icon */}
          <div style={{
            position: "relative",
            zIndex: 10,
            animation: "pulse 2s ease-in-out infinite"
          }}>
            <Calculator style={{
              width: "40px",
              height: "40px",
              color: "#60a5fa"
            }} />
          </div>
        </div>

        {/* Animated Number Display */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          fontSize: "28px",
          fontFamily: "monospace",
          color: "var(--color)"
        }}>
          <span style={{ animation: "fade-in 0.5s ease-in-out infinite alternate", animationDelay: "0s" }}>$</span>
          <span style={{ animation: "fade-in 0.5s ease-in-out infinite alternate", animationDelay: "0.1s" }}>1</span>
          <span style={{ animation: "fade-in 0.5s ease-in-out infinite alternate", animationDelay: "0.2s" }}>2</span>
          <span style={{ animation: "fade-in 0.5s ease-in-out infinite alternate", animationDelay: "0.3s" }}>3</span>
          <span style={{ animation: "fade-in 0.5s ease-in-out infinite alternate", animationDelay: "0.4s" }}>,</span>
          <span style={{ animation: "fade-in 0.5s ease-in-out infinite alternate", animationDelay: "0.5s" }}>4</span>
          <span style={{ animation: "fade-in 0.5s ease-in-out infinite alternate", animationDelay: "0.6s" }}>5</span>
          <span style={{ animation: "fade-in 0.5s ease-in-out infinite alternate", animationDelay: "0.7s" }}>6</span>
        </div>

        {/* Loading Dots */}
        <div style={{
          display: "flex",
          gap: "8px",
          marginTop: "8px"
        }}>
          <div style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: "#3b82f6",
            animation: "bounce 1.4s ease-in-out infinite",
            animationDelay: "0s"
          }} />
          <div style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: "#10b981",
            animation: "bounce 1.4s ease-in-out infinite",
            animationDelay: "0.2s"
          }} />
          <div style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: "#8b5cf6",
            animation: "bounce 1.4s ease-in-out infinite",
            animationDelay: "0.4s"
          }} />
        </div>

        {/* Message */}
        <p style={{
          fontSize: "16px",
          color: "var(--color)",
          marginTop: "16px",
          animation: "pulse 2s ease-in-out infinite"
        }}>
          {message}
        </p>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes spin-reverse {
            from { transform: rotate(360deg); }
            to { transform: rotate(0deg); }
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          @keyframes bounce {
            0%, 80%, 100% { 
              transform: translateY(0);
            }
            40% { 
              transform: translateY(-12px);
            }
          }

          @keyframes fade-in {
            from { opacity: 0.3; }
            to { opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
}
